import { AutoModeOutcome } from '../../flora.type';
import { ToolCallTagVariant } from './chat.constants';
import type { AutoModeTag } from './chat.constants';

/**
 * Known external-integration tool-name prefixes and the vendor label to show
 * for them. Matched case-insensitively as a whole leading segment — the prefix
 * must be the entire name or be followed by `.`/`_` — so both dotted
 * (`google_slides.create_deck`) and underscored (`google_slides_create`) forms
 * resolve to the same vendor, while lookalikes like `githubenterprise` do not.
 * Order matters only in that the first match wins; keep prefixes mutually
 * exclusive.
 */
const VENDOR_PREFIXES: ReadonlyArray<{ prefix: string; label: string }> = [
  { prefix: 'google_slides', label: 'Google Slides' },
  { prefix: 'google_docs', label: 'Google Docs' },
  { prefix: 'google_sheets', label: 'Google Sheets' },
  { prefix: 'google_drive', label: 'Google Drive' },
  { prefix: 'jira', label: 'Jira' },
  { prefix: 'ado', label: 'Azure DevOps' },
  { prefix: 'slack', label: 'Slack' },
  { prefix: 'confluence', label: 'Confluence' },
  { prefix: 'github', label: 'GitHub' },
];

/**
 * Vendor shown for Flora's own tools — anything without a recognized external
 * integration prefix (e.g. `search_processes`, `read_dashboard`).
 */
const DEFAULT_VENDOR = 'Bloomfilter';

/**
 * Resolve the vendor label for a tool call from its name. Recognized external
 * integrations map to their product name; every other (Flora-native) tool maps
 * to {@link DEFAULT_VENDOR}. An empty name yields an empty string so the caller
 * can omit the label rather than mislabel it.
 *
 * @param {string} toolName - The raw `tool_name` from a tool call.
 * @returns {string} The vendor label, or `''` when the name is empty.
 */
function getToolVendor(toolName: string): string {
  if (!toolName) {
    return '';
  }
  const normalized = toolName.toLowerCase();
  for (const { prefix, label } of VENDOR_PREFIXES) {
    // Require the prefix to be a whole segment (exact, or followed by `.`/`_`)
    // so `github_create` matches but `githubenterprise` does not.
    if (normalized === prefix || normalized.startsWith(`${prefix}.`) || normalized.startsWith(`${prefix}_`)) {
      return label;
    }
  }
  return DEFAULT_VENDOR;
}

/**
 * Join distinct vendor labels into prose, preserving first-appearance order and
 * de-duplicating: `[]` → `''`, `['Jira']` → `'Jira'`, `['A','B']` → `'A and B'`,
 * `['A','B','C']` → `'A, B and C'`. Falsy entries are dropped first.
 *
 * @param {string[]} vendors - Vendor labels in appearance order (may repeat).
 * @returns {string} The human-readable summary phrase.
 */
function summarizeVendors(vendors: string[]): string {
  const unique = [...new Set(vendors.filter(Boolean))];
  if (unique.length === 0) {
    return '';
  }
  if (unique.length === 1) {
    return unique[0];
  }
  return `${unique.slice(0, -1).join(', ')} and ${unique[unique.length - 1]}`;
}

/**
 * Tag shown for each auto-mode outcome. Shared by the grouped tool-call rows
 * and the single tool-call card so their labels, tooltips and colors stay in
 * sync. A `Danger` tag replaces the "Drafted" tag, since the call never ran.
 */
const AUTO_MODE_TAGS: Readonly<Record<AutoModeOutcome, AutoModeTag>> = {
  [AutoModeOutcome.Approved]: {
    label: 'Auto-approved',
    tooltip: 'Auto mode ran this without asking',
    variant: ToolCallTagVariant.Subtle,
  },
  [AutoModeOutcome.Blocked]: {
    label: 'Blocked by auto mode',
    tooltip: "Auto mode judged this risky and didn't run it",
    variant: ToolCallTagVariant.Danger,
  },
  [AutoModeOutcome.Unavailable]: {
    label: 'Risk check unavailable',
    tooltip: "The risk check was unavailable, so this didn't run",
    variant: ToolCallTagVariant.Danger,
  },
  [AutoModeOutcome.Invalid]: {
    label: 'Blocked by validation',
    tooltip: "Flora checked this before running it and it couldn't succeed",
    variant: ToolCallTagVariant.Danger,
  },
};

/**
 * Resolve the tag to show for a tool call's auto-mode outcome.
 *
 * @param {AutoModeOutcome | undefined} outcome - The call's `auto_mode`, if auto mode decided on it
 * @returns {AutoModeTag | null} The tag to render, or null when auto mode made no decision
 * @example
 * getAutoModeTag(AutoModeOutcome.Blocked)?.label // 'Blocked by auto mode'
 * getAutoModeTag(undefined) // null
 */
function getAutoModeTag(outcome: AutoModeOutcome | undefined): AutoModeTag | null {
  return outcome ? (AUTO_MODE_TAGS[outcome] ?? null) : null;
}

export { getAutoModeTag, getToolVendor, summarizeVendors };
